import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useListFilters } from '@/client/hooks/useListFilters';
import { Crosshair, Plus, Search, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  ACQUISITION_STAGE_BADGE,
  ACQUISITION_STAGE_LABEL,
  AVAILABILITY_BADGE,
  AVAILABILITY_LABEL,
  CLAIM_STATUS_LABEL,
  OUTREACH_CHANNEL_LABEL,
  SALES_GOAL_LABEL,
  SALES_GOALS,
  SALES_VERTICALS,
  SPEND_BAND_LABEL,
  SUBJECT_KIND_LABEL,
  VERTICAL_LABEL,
  isInPool,
  onPlatform,
  sourceKind,
  sourceLabel,
  type Availability,
  type SalesGoal,
  type SalesImportSkip,
  type SalesNearMatch,
  type SalesSubject,
  type SourceKind,
  type SpendBand,
  type SubjectKind,
  type SalesVertical,
} from '@/client/types/sales';
import { SalesPageShell } from './components/SalesPageShell';
import { DuplicateMatchesDialog } from './components/DuplicateMatchesDialog';
import { TagChips, TagInput } from './components/TagInput';
import { getApiErrorCode, getApiErrorField } from '@/client/utils/errors';
import { CopyLinkButton, Segmented, StatusBadge, btnGhost, btnPrimary, compactGhs, selectCls, timeLeftLabel, toastSales } from './components/sales-ui';
import {
  useClaimSubjectMutation,
  useClearCooldownMutation,
  useCreateSubjectMutation,
  useSalesTagsQuery,
  useImportSubjectsMutation,
  useSalesClaimsQuery,
  useSalesMeQuery,
  useSalesOffersQuery,
  useSalesOutreachQuery,
  useSalesPolicyQuery,
  useSalesPurposesQuery,
  useSalesRepsQuery,
  useSalesSubjectsQuery,
} from '@/client/api/sales/sales';
import { previewCsvImport } from './sales-store';
import { promptActAs, useCurrentRep } from './sales-store';
import {
  achievedGoalsFor,
  availabilityFor,
  bestFitFor,
  displayClaimStatus,
  fitScoreFor,
  lockingClaimFor,
} from './sales-live';
import { DEFAULT_SALES_POLICY } from '@/client/types/sales';

const emptyManual = {
  displayName: '',
  phone: '',
  email: '',
  kind: 'person' as SubjectKind,
  region: 'Accra',
  vertical: 'homes' as SalesVertical,
  spendBand: 'unknown' as SpendBand,
  notes: '',
  tags: [] as string[],
  alreadyOnPlatform: false,
  uid: '',
  oid: '',
};

const SAMPLE_CSV = `name,phone,email,kind,region,vertical,spendBand,tags
"Ama Realty, Ltd",0244000111,ama@x.com,business,Accra,homes,high,"hot;agency"
Kofi Mensah,0244000222,kofi@x.com,person,East Legon,homes,mid,referral`;

function matchesQuery(s: SalesSubject, q: string): boolean {
  const n = q.trim().toLowerCase();
  if (!n) return true;
  const uid = s.platform?.uid != null ? String(s.platform.uid) : '';
  const oid = s.platform?.oid != null ? String(s.platform.oid) : '';
  return [s.displayName, s.phone, s.email, uid, oid, ...s.matchKeys].some((v) => v?.toLowerCase().includes(n));
}

const FILTER_SELECT =
  'h-10 w-full rounded-lg border border-gray-400 bg-white px-3 text-sm text-gray-900 focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900';

function FilterField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-800">
        {label}
      </label>
      {children}
    </div>
  );
}

type PoolLayout = 'cards' | 'list' | 'table';
const LAYOUT_KEY = 'ff-admin.sales.prospects.layout';

function loadLayout(): PoolLayout {
  try {
    const v = localStorage.getItem(LAYOUT_KEY);
    if (v === 'cards' || v === 'list' || v === 'table') return v;
  } catch {
    /* private mode */
  }
  return 'cards';
}

function ClaimAction({
  available,
  mine,
  locked,
  cooling,
  holderName,
  until,
  coolingUntil,
  onClaim,
  onOpen,
  onClearCooldown,
  subjectId,
}: {
  available: boolean;
  mine: boolean;
  locked: boolean;
  cooling: boolean;
  holderName?: string;
  until?: string;
  coolingUntil?: string;
  onClaim: () => void;
  onOpen: () => void;
  /** Managers only: set when the viewer may end the cooldown early. */
  onClearCooldown?: () => void;
  subjectId: string;
}) {
  if (available) {
    return (
      <button type="button" className={btnPrimary} onClick={onClaim}>
        Claim exclusive
      </button>
    );
  }
  if (locked && mine) {
    return (
      <div className="flex flex-wrap gap-2">
        <button type="button" className={btnGhost} onClick={onOpen}>
          Open workspace
        </button>
        <Link to={`/admin/sales/communications?subject=${subjectId}`} className={btnGhost}>
          Message
        </Link>
      </div>
    );
  }
  if (locked) {
    return (
      <p className="text-xs text-gray-600">
        Exclusive to {holderName}
        {until ? ` until ${until}` : ''}. Nobody else can claim.
      </p>
    );
  }
  if (cooling)
    return (
      <div className="space-y-2">
        <p className="text-xs text-gray-600">
          Cooling{coolingUntil ? ` until ${coolingUntil}` : ''} — a recent claim ended. Opens up for anyone then.
        </p>
        {onClearCooldown && (
          <button type="button" className={btnGhost} onClick={onClearCooldown}>
            Clear cooldown
          </button>
        )}
      </div>
    );
  return <p className="text-xs text-gray-600">Do not contact.</p>;
}

export default function SalesPoolPage() {
  const navigate = useNavigate();
  const rep = useCurrentRep();
  const me = useSalesMeQuery();
  const canWrite = Boolean(rep) && me.data?.canWriteProspects !== false;
  const canClaim = Boolean(rep) && me.data?.canWriteClaims !== false;
  // Filters live in the URL (useListFilters) so a filtered pool view is
  // shareable and survives reload. The derived consts + setter shims below keep
  // every downstream read and onChange handler unchanged.
  const { searchTerm: q, handleSearch: setQ, filters, handleFilterChange, page, handlePageChange } = useListFilters({
    initialFilters: {
      kind: 'all' as SubjectKind | 'all',
      goal: 'all' as SalesGoal | 'all',
      vertical: 'all' as SalesVertical | 'all',
      origin: 'all' as SourceKind | 'all',
      presence: 'all' as 'all' | 'on_platform' | 'to_onboard',
      spend: 'all' as SpendBand | 'all',
      tag: 'all',
      availableOnly: '', // '' = off (default), '1' = on
      territoryOn: '1', // '1' = on (default), '' = off
    },
  });
  const kind = filters.kind;
  const goal = filters.goal;
  const vertical = filters.vertical;
  const origin = filters.origin;
  const presence = filters.presence;
  const spend = filters.spend;
  const tag = filters.tag;
  const availableOnly = filters.availableOnly === '1';
  const territoryOn = filters.territoryOn !== '';
  const setKind = (v: string) => handleFilterChange('kind', v);
  const setGoal = (v: string) => handleFilterChange('goal', v);
  const setVertical = (v: string) => handleFilterChange('vertical', v);
  const setOrigin = (v: string) => handleFilterChange('origin', v);
  const setPresence = (v: string) => handleFilterChange('presence', v);
  const setSpend = (v: string) => handleFilterChange('spend', v);
  const setTag = (v: string) => handleFilterChange('tag', v);
  const setAvailableOnly = (on: boolean) => handleFilterChange('availableOnly', on ? '1' : '');
  const setTerritoryOn = (on: boolean) => handleFilterChange('territoryOn', on ? '1' : '');
  const tagsQ = useSalesTagsQuery();
  const limit = 50;
  // Page lives in the URL (via useListFilters) so a paged pool view is shareable;
  // the hook resets it to 1 whenever a filter changes.
  const offset = (page - 1) * limit;
  const purposesQ = useSalesPurposesQuery();
  const offersQ = useSalesOffersQuery();
  const policyQ = useSalesPolicyQuery();
  const repsQ = useSalesRepsQuery();
  const claimsQ = useSalesClaimsQuery({ limit: 200 });
  const outreachQ = useSalesOutreachQuery({ limit: 200 });
  const subjectsQ = useSalesSubjectsQuery({
    q: q || undefined,
    kind,
    spend,
    vertical,
    origin,
    presence,
    tag: tag === 'all' ? undefined : tag,
    availability: availableOnly ? 'available' : 'all',
    limit,
    offset,
  });
  const claimMut = useClaimSubjectMutation();
  const clearMut = useClearCooldownMutation();
  // Managers override a cooldown when a claim lapsed because nobody acted on it.
  const clearCooldown = me.data?.canManageRules
    ? (s: SalesSubject) =>
        clearMut.mutate(s.id, {
          onSuccess: () => toast.success(`${s.displayName} is back in the pool`),
          onError: (e) => toastSales(e),
        })
    : undefined;
  const createMut = useCreateSubjectMutation();
  const importMut = useImportSubjectsMutation();
  const purposes = purposesQ.data ?? [];
  const offers = offersQ.data ?? [];
  const policy = policyQ.data ?? DEFAULT_SALES_POLICY;
  const claims = claimsQ.data?.claims ?? [];
  const outreaches = outreachQ.data?.outreach ?? [];
  const subjects = subjectsQ.data?.subjects ?? [];
  const total = subjectsQ.data?.total ?? 0;
  const weights = policy.fitWeights;
  const now = Date.now();
  const [claiming, setClaiming] = useState<SalesSubject | null>(null);
  const [purposeId, setPurposeId] = useState('');
  const [offerId, setOfferId] = useState('');
  const [csv, setCsv] = useState('');
  const [csvFileName, setCsvFileName] = useState('');
  const [csvDrag, setCsvDrag] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<{ imported: number; skipped: SalesImportSkip[] } | null>(
    null,
  );
  const [addOpen, setAddOpen] = useState(false);
  const [dupes, setDupes] = useState<SalesNearMatch[] | null>(null);
  const [manual, setManual] = useState(emptyManual);
  const [layout, setLayout] = useState<PoolLayout>(loadLayout);

  const purposeById = (id: string) => purposes.find((p) => p.id === id);
  const repById = (id: string) => (repsQ.data ?? []).find((r) => r.id === id);
  const latestOutreach = (subjectId: string) =>
    outreaches.filter((o) => o.subjectId === subjectId).sort((a, b) => b.at.localeCompare(a.at))[0];


  useEffect(() => {
    try {
      localStorage.setItem(LAYOUT_KEY, layout);
    } catch {
      /* private mode */
    }
  }, [layout]);

  const pooled = useMemo(() => subjects.filter((s) => isInPool(s)), [subjects]);

  const rows = useMemo(() => {
    return pooled
      .filter((s) => matchesQuery(s, q))
      .filter((s) => (kind === 'all' ? true : s.kind === kind))
      .filter((s) => (spend === 'all' ? true : s.spendBand === spend))
      .filter((s) => (vertical === 'all' ? true : s.verticals.includes(vertical)))
      .filter((s) => (origin === 'all' ? true : sourceKind(s.source) === origin))
      .filter((s) => {
        if (presence === 'on_platform') return onPlatform(s);
        if (presence === 'to_onboard') return !onPlatform(s);
        return true;
      })
      .filter((s) => (tag === 'all' ? true : (s.tags ?? []).includes(tag)))
      .filter((s) => {
          if (!territoryOn || !rep) return true;
          const regionOk =
            rep.territory.regions.length === 0 || s.areas.some((a) => rep.territory.regions.includes(a.region));
          const vertOk =
            rep.territory.verticals.length === 0 || s.verticals.some((v) => rep.territory.verticals.includes(v));
          return regionOk && vertOk;
      })
      .filter((s) => {
        const av = availabilityFor(s, claims);
        if (availableOnly) return av === 'available';
        return true;
      })
      .filter((s) => {
        if (goal === 'all') return true;
        return !achievedGoalsFor(s.id, claims).includes(goal);
      })
      .sort((a, b) => {
        const sa = goal === 'all' ? bestFitFor(a, claims, weights).score : fitScoreFor(a, goal, claims, weights);
        const sb = goal === 'all' ? bestFitFor(b, claims, weights).score : fitScoreFor(b, goal, claims, weights);
        return sb - sa;
      });
  }, [pooled, q, kind, spend, vertical, origin, presence, tag, territoryOn, availableOnly, goal, rep, weights, claims]);

  const availableN = pooled.filter((s) => availabilityFor(s, claims) === 'available').length;
  const heldN = pooled.filter((s) => availabilityFor(s, claims) === 'locked').length;
  const onFfN = pooled.filter((s) => onPlatform(s)).length;
  const filtersOn =
    Boolean(q.trim()) ||
    vertical !== 'all' ||
    origin !== 'all' ||
    presence !== 'all' ||
    tag !== 'all' ||
    kind !== 'all' ||
    goal !== 'all' ||
    spend !== 'all' ||
    availableOnly ||
    !territoryOn;

  const resetFilters = () => {
    setQ('');
    setVertical('all');
    setOrigin('all');
    setPresence('all');
    setTag('all');
    setKind('all');
    setGoal('all');
    setSpend('all');
    setAvailableOnly(false);
    setTerritoryOn(true);
  };

  const csvPreview = useMemo(() => (importOpen && csv.trim() ? previewCsvImport(csv) : []), [importOpen, csv, subjects]);
  const csvReady = csvPreview.filter((r) => !r.error).length;

  const openImport = () => {
    setCsv('');
    setCsvFileName('');
    setCsvDrag(false);
    setImportResult(null);
    setAddOpen(false);
    setImportOpen(true);
  };

  const readCsvFile = (file: File) => {
    file
      .text()
      .then((text) => {
        setCsv(text);
        setCsvFileName(file.name);
        setImportResult(null);
      })
      .catch((e) => toastSales(e));
  };

  const openClaim = (s: SalesSubject) => {
    if (!rep) {
      promptActAs('claim a prospect');
      return;
    }
    const remaining = purposes.filter((p) => p.active && !achievedGoalsFor(s.id, claims).includes(p.goal));
    setPurposeId(remaining[0]?.id ?? '');
    setOfferId('');
    setClaiming(s);
  };

  const claimDays = purposeById(purposeId)?.defaultClaimDays ?? 14;

  const submitClaim = () => {
    if (!claiming) return;
    if (!canClaim) {
      if (!rep) promptActAs('claim a prospect');
      else toast.error('You do not have permission to claim.');
      return;
    }
    claimMut.mutate(
      { subjectId: claiming.id, purposeId, offerId: offerId || undefined },
      {
        onSuccess: () => {
          toast.success(`Claimed ${claiming.displayName} for ${claimDays} exclusive days`);
          const id = claiming.id;
          setClaiming(null);
          navigate(`/admin/sales/prospects/${id}`);
        },
        onError: (e) => toastSales(e),
      },
    );
  };

  const runImport = () => {
    if (!canWrite) {
      if (!rep) promptActAs('import a list');
      else toast.error('You do not have permission to add prospects.');
      return;
    }
    importMut.mutate(csv, {
      onSuccess: (result) => {
        const imported = result.importedCount ?? result.imported?.length ?? 0;
        const skipped = result.skipped ?? [];
        setImportResult({ imported, skipped });
        if (imported) {
          toast.success(`Imported ${imported}${skipped.length ? `, skipped ${skipped.length}` : ''}`);
        } else if (skipped.length) {
          toast.error(`Nothing imported. ${skipped.length} row${skipped.length === 1 ? '' : 's'} skipped.`);
        }
      },
      onError: (e) => toastSales(e),
    });
  };

  const submitManual = (confirmNew = false) => {
    if (!canWrite) {
      if (!rep) promptActAs('add a prospect');
      else toast.error('You do not have permission to add prospects.');
      return;
    }
    createMut.mutate(
      {
        displayName: manual.displayName,
        phone: manual.phone || undefined,
        email: manual.email || undefined,
        kind: manual.kind,
        areas: manual.region ? [{ region: manual.region }] : [],
        verticals: [manual.vertical],
        spendBand: manual.spendBand,
        notes: manual.notes || undefined,
        tags: manual.tags.length ? manual.tags : undefined,
        confirmNew: confirmNew || undefined,
        platform:
          manual.uid || manual.oid
            ? { uid: manual.uid ? Number(manual.uid) : undefined, oid: manual.oid ? Number(manual.oid) : undefined }
            : undefined,
      },
      {
        onSuccess: (created) => {
          setDupes(null);
          toast.success(`Added ${created.displayName || manual.displayName}`);
          setAddOpen(false);
          setManual(emptyManual);
          navigate(`/admin/sales/prospects/${created.id}`);
        },
        onError: (e) => {
          if (getApiErrorCode(e) === 'possible_duplicate') {
            setDupes(getApiErrorField<SalesNearMatch[]>(e, 'matches') ?? []);
            return;
          }
          toastSales(e);
        },
      },
    );
  };

  return (
    <SalesPageShell
      title="Pool"
      description="Query the pool or add someone. Search is for people already listing elsewhere — those paying to list are halfway to FeelyFeely."
      crumbs={[{ label: 'Prospects', href: '/admin/sales/prospects/overview' }, { label: 'Pool' }]}
      icon={Crosshair}
      stats={[
        { key: 'shown', label: 'Showing', value: total ? `${rows.length} / ${total}` : rows.length },
        { key: 'available', label: 'Open to claim', value: availableN },
        { key: 'held', label: 'Held exclusive', value: heldN },
        { key: 'onff', label: 'On FeelyFeely', value: onFfN },
      ]}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <CopyLinkButton label="Copy link" />
          <button type="button" className={btnGhost} onClick={() => navigate('/admin/sales/prospects/search')}>
            <Search className="h-4 w-4" />
            Search listings
          </button>
          <button
            type="button"
            className={btnGhost}
            onClick={() => (rep ? openImport() : promptActAs('import a list'))}
            disabled={Boolean(rep) && !canWrite}
          >
            <Upload className="h-4 w-4" />
            Import list
          </button>
          <button
            type="button"
            className={btnPrimary}
            disabled={Boolean(rep) && !canWrite}
            onClick={() => {
              if (!rep) {
                promptActAs('add a prospect');
                return;
              }
              setManual(emptyManual);
              setAddOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add prospect
          </button>
        </div>
      }
    >
      <div className="mb-5 space-y-4" role="search" aria-label="Prospect pool">
        <div>
          <label htmlFor="prospect-search" className="mb-1.5 block text-sm font-medium text-gray-900">
            Search
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" aria-hidden />
            <input
              id="prospect-search"
              type="search"
              autoComplete="off"
              className="h-11 w-full rounded-lg border border-gray-400 bg-white py-2 pl-10 pr-11 text-sm text-gray-900 placeholder:text-gray-500 focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, phone, email, or FeelyFeely id"
              aria-describedby="prospect-search-hint"
            />
            {q ? (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                aria-label="Clear search"
              >
                <X className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <p id="prospect-search-hint" className="mt-1.5 text-xs text-gray-600">
            Matches people and orgs already in the pool, including those held exclusive. To find listers who are not in the pool yet, use Search.
          </p>
        </div>

        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-sm font-medium text-gray-900">Filters</h2>
            <div className="flex flex-wrap items-center gap-3">
              <div role="group" aria-label="Result layout">
                <Segmented
                  value={layout}
                  onChange={setLayout}
                  options={[
                    { id: 'cards', label: 'Cards' },
                    { id: 'list', label: 'List' },
                    { id: 'table', label: 'Table' },
                  ]}
                />
              </div>
              {filtersOn ? (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="text-sm font-medium text-gray-800 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                >
                  Clear all
                </button>
              ) : null}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
            <FilterField id="prospect-category" label="Category">
              <select
                id="prospect-category"
                className={FILTER_SELECT}
                value={vertical}
                onChange={(e) => setVertical(e.target.value as SalesVertical | 'all')}
              >
                <option value="all">All</option>
                {SALES_VERTICALS.map((v) => (
                  <option key={v} value={v}>
                    {VERTICAL_LABEL[v]}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField id="prospect-source" label="Source">
              <select
                id="prospect-source"
                className={FILTER_SELECT}
                value={origin}
                onChange={(e) => setOrigin(e.target.value as SourceKind | 'all')}
              >
                <option value="all">All</option>
                <option value="intelligence">Intelligence</option>
                <option value="research">Research</option>
                <option value="contributed">Contributed</option>
                <option value="platform">On FeelyFeely</option>
              </select>
            </FilterField>
            <FilterField id="prospect-platform" label="Platform">
              <select
                id="prospect-platform"
                className={FILTER_SELECT}
                value={presence}
                onChange={(e) => setPresence(e.target.value as 'all' | 'on_platform' | 'to_onboard')}
              >
                <option value="all">All</option>
                <option value="on_platform">Already on FeelyFeely</option>
                <option value="to_onboard">To onboard</option>
              </select>
            </FilterField>
            <FilterField id="prospect-tag" label="Tag">
              <select
                id="prospect-tag"
                className={FILTER_SELECT}
                value={tag}
                onChange={(e) => setTag(e.target.value)}
              >
                <option value="all">Any tag</option>
                {(tagsQ.data ?? []).map((t) => (
                  <option key={t.tag} value={t.tag}>
                    {t.tag} ({t.count})
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField id="prospect-kind" label="Kind">
              <select
                id="prospect-kind"
                className={FILTER_SELECT}
                value={kind}
                onChange={(e) => setKind(e.target.value as SubjectKind | 'all')}
              >
                <option value="all">All</option>
                {(['person', 'organization', 'business'] as SubjectKind[]).map((k) => (
                  <option key={k} value={k}>
                    {SUBJECT_KIND_LABEL[k]}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField id="prospect-goal" label="Goal still open">
              <select
                id="prospect-goal"
                className={FILTER_SELECT}
                value={goal}
                onChange={(e) => setGoal(e.target.value as SalesGoal | 'all')}
              >
                <option value="all">Any</option>
                {SALES_GOALS.map((g) => (
                  <option key={g} value={g}>
                    {SALES_GOAL_LABEL[g]}
                  </option>
                ))}
              </select>
            </FilterField>
            <FilterField id="prospect-spend" label="Spend">
              <select
                id="prospect-spend"
                className={FILTER_SELECT}
                value={spend}
                onChange={(e) => setSpend(e.target.value as SpendBand | 'all')}
              >
                <option value="all">Any</option>
                {(['high', 'mid', 'low', 'unknown'] as SpendBand[]).map((b) => (
                  <option key={b} value={b}>
                    {SPEND_BAND_LABEL[b]}
                  </option>
                ))}
              </select>
            </FilterField>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-gray-400 bg-white px-3 text-sm text-gray-900 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-slate-900">
              <input
                id="prospect-open"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-400 text-slate-900 focus:ring-slate-900"
                checked={availableOnly}
                onChange={(e) => setAvailableOnly(e.target.checked)}
              />
              Open to claim
            </label>
            <label className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg border border-gray-400 bg-white px-3 text-sm text-gray-900 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-slate-900">
              <input
                id="prospect-territory"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-400 text-slate-900 focus:ring-slate-900"
                checked={territoryOn}
                onChange={(e) => setTerritoryOn(e.target.checked)}
              />
              My territory
            </label>
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-6 py-14 text-center">
          <p className="text-sm text-gray-600">{q.trim() ? 'No matches for that query.' : 'Nothing in this slice of the pool.'}</p>
          <p className="mt-1 text-sm text-gray-400">Add them, import a list, or find people already listing elsewhere.</p>
          <div className="mt-4 flex justify-center gap-2">
            <button
              type="button"
              className={btnPrimary}
              onClick={() => {
                setManual({ ...emptyManual, displayName: q.trim() });
                setAddOpen(true);
              }}
            >
              Add prospect
            </button>
            <button type="button" className={btnGhost} onClick={openImport}>
              Import list
            </button>
            <button type="button" className={btnGhost} onClick={() => navigate('/admin/sales/prospects/search')}>
              Search listings
            </button>
          </div>
        </div>
      ) : layout === 'table' ? (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <caption className="sr-only">Prospects in this query</caption>
            <thead className="border-b border-gray-200 text-left text-xs font-medium text-gray-800">
              <tr>
                <th scope="col" className="px-4 py-3">
                  Name
                </th>
                <th scope="col" className="px-4 py-3">
                  Stage
                </th>
                <th scope="col" className="px-4 py-3">
                  FeelyFeely
                </th>
                <th scope="col" className="px-4 py-3">
                  Conversation
                </th>
                <th scope="col" className="px-4 py-3">
                  Exclusive
                </th>
                <th scope="col" className="px-4 py-3 text-right">
                  Value
                </th>
                <th scope="col" className="px-4 py-3">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const av = availabilityFor(s, claims);
                const lock = lockingClaimFor(s.id, claims);
                const holder = lock ? repById(lock.ownerId) : undefined;
                const mine = lock?.ownerId === rep?.id;
                const scored = goal === 'all' ? bestFitFor(s, claims, weights) : { goal, score: fitScoreFor(s, goal, claims, weights) };
                const purposeValue = purposes.find((p) => p.active && p.goal === scored.goal)?.estimatedValueGhs ?? 0;
                const last = latestOutreach(s.id);
                const snap = s.platform;
                const onFf = onPlatform(s);
                const status = lock ? displayClaimStatus(lock) : null;
                return (
                  <tr key={s.id} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-3">
                      <Link to={`/admin/sales/prospects/${s.id}`} className="font-medium text-gray-900 hover:text-slate-700">
                        {s.displayName}
                      </Link>
                      <TagChips tags={s.tags} className="mt-1" />
                      <div className="text-xs text-gray-600">
                        {SUBJECT_KIND_LABEL[s.kind]} · {s.areas.map((a) => a.region).join(', ')} ·{' '}
                        {s.verticals.map((v) => VERTICAL_LABEL[v]).join(', ')}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge label={AVAILABILITY_LABEL[av as Availability]} className={AVAILABILITY_BADGE[av]} />
                        <StatusBadge
                          label={ACQUISITION_STAGE_LABEL[s.acquisitionStage]}
                          className={ACQUISITION_STAGE_BADGE[s.acquisitionStage]}
                        />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {onFf
                        ? `On · ${snap?.uid != null ? `uid ${snap.uid}` : snap?.oid != null ? `oid ${snap.oid}` : 'linked'}${
                            snap?.listingCount ? ` · ${snap.listingCount} listings` : ''
                          }`
                        : 'Not yet'}
                    </td>
                    <td className="max-w-xs px-4 py-3 text-gray-800">
                      {lock
                        ? `${CLAIM_STATUS_LABEL[status ?? lock.status]}${purposeById(lock.purposeId) ? ` · ${purposeById(lock.purposeId)?.name}` : ''}`
                        : last
                          ? `${OUTREACH_CHANNEL_LABEL[last.channel]} · ${new Date(last.at).toLocaleDateString('en-GB')}`
                          : 'None yet'}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {lock ? `${mine ? 'Yours' : holder?.name ?? lock.ownerId} · ${timeLeftLabel(lock.expiresAt, now)}` : '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-gray-900">{compactGhs(purposeValue)}</td>
                    <td className="min-w-[9.5rem] px-4 py-3">
                      <ClaimAction
                        available={av === 'available'}
                        mine={mine}
                        locked={Boolean(lock)}
                        cooling={av === 'cooling'}
                        holderName={holder?.name ?? lock?.ownerId}
                        until={lock ? new Date(lock.expiresAt).toLocaleDateString('en-GB') : undefined}
                        coolingUntil={s.subjectCooldownUntil ? new Date(s.subjectCooldownUntil).toLocaleDateString('en-GB') : undefined}
                        onClaim={() => openClaim(s)}
                        onClearCooldown={clearCooldown ? () => clearCooldown(s) : undefined}
                        onOpen={() => navigate(`/admin/sales/prospects/${s.id}`)}
                        subjectId={s.id}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : layout === 'list' ? (
        <ul className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white">
          {rows.map((s) => {
            const av = availabilityFor(s, claims);
            const lock = lockingClaimFor(s.id, claims);
            const holder = lock ? repById(lock.ownerId) : undefined;
            const mine = lock?.ownerId === rep?.id;
            const scored = goal === 'all' ? bestFitFor(s, claims, weights) : { goal, score: fitScoreFor(s, goal, claims, weights) };
            const purposeValue = purposes.find((p) => p.active && p.goal === scored.goal)?.estimatedValueGhs ?? 0;
            const last = latestOutreach(s.id);
            const snap = s.platform;
            const onFf = onPlatform(s);
            const status = lock ? displayClaimStatus(lock) : null;
            return (
              <li key={s.id} className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/admin/sales/prospects/${s.id}`} className="font-semibold text-gray-900 hover:text-slate-700">
                      {s.displayName}
                    </Link>
                    <StatusBadge label={AVAILABILITY_LABEL[av as Availability]} className={AVAILABILITY_BADGE[av]} />
                    <TagChips tags={s.tags} />
                    <StatusBadge
                      label={ACQUISITION_STAGE_LABEL[s.acquisitionStage]}
                      className={ACQUISITION_STAGE_BADGE[s.acquisitionStage]}
                    />
                    <span className="text-sm tabular-nums text-gray-900">{compactGhs(purposeValue)}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    {SUBJECT_KIND_LABEL[s.kind]} · {s.areas.map((a) => a.region).join(', ')} ·{' '}
                    {s.verticals.map((v) => VERTICAL_LABEL[v]).join(', ')} · {sourceLabel(s.source)}
                  </p>
                  <p className="mt-2 text-sm text-gray-800">
                    {onFf ? (
                      <>
                        On FeelyFeely
                        {snap?.uid != null ? ` · uid ${snap.uid}` : snap?.oid != null ? ` · oid ${snap.oid}` : ''}
                        {` · ${snap?.listingCount ?? 0} listings`}
                        {snap?.lifetimePaidGhs ? ` · paid ${compactGhs(snap.lifetimePaidGhs)}` : ''}
                      </>
                    ) : (
                      'Not on FeelyFeely yet'
                    )}
                    {' · '}
                    {lock ? (
                      <>
                        {CLAIM_STATUS_LABEL[status ?? lock.status]}
                        {purposeById(lock.purposeId) ? ` · ${purposeById(lock.purposeId)?.name}` : ''}
                        {' · '}
                        {mine ? 'your exclusive' : `held by ${holder?.name ?? lock.ownerId}`}
                        {' · '}
                        {timeLeftLabel(lock.expiresAt, now)}
                      </>
                    ) : last ? (
                      <>
                        {OUTREACH_CHANNEL_LABEL[last.channel]} · {new Date(last.at).toLocaleDateString('en-GB')}
                        {last.body ? ` — ${last.body.slice(0, 90)}` : ''}
                      </>
                    ) : (
                      'No conversation yet'
                    )}
                  </p>
                </div>
                <div className="shrink-0 sm:pt-0.5">
                  <ClaimAction
                    available={av === 'available'}
                    mine={mine}
                    locked={Boolean(lock)}
                    cooling={av === 'cooling'}
                    holderName={holder?.name ?? lock?.ownerId}
                    until={lock ? new Date(lock.expiresAt).toLocaleDateString('en-GB') : undefined}
                    coolingUntil={s.subjectCooldownUntil ? new Date(s.subjectCooldownUntil).toLocaleDateString('en-GB') : undefined}
                    onClaim={() => openClaim(s)}
                    onClearCooldown={clearCooldown ? () => clearCooldown(s) : undefined}
                    onOpen={() => navigate(`/admin/sales/prospects/${s.id}`)}
                        subjectId={s.id}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((s) => {
            const av = availabilityFor(s, claims);
            const lock = lockingClaimFor(s.id, claims);
            const holder = lock ? repById(lock.ownerId) : undefined;
            const mine = lock?.ownerId === rep?.id;
            const scored = goal === 'all' ? bestFitFor(s, claims, weights) : { goal, score: fitScoreFor(s, goal, claims, weights) };
            const purposeValue = purposes.find((p) => p.active && p.goal === scored.goal)?.estimatedValueGhs ?? 0;
            const last = latestOutreach(s.id);
            const snap = s.platform;
            const onFf = onPlatform(s);
            const status = lock ? displayClaimStatus(lock) : null;
            return (
              <div key={s.id} className="flex flex-col rounded-xl border border-gray-200 bg-white p-4">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <Link to={`/admin/sales/prospects/${s.id}`} className="font-semibold text-gray-900 hover:text-slate-700">
                      {s.displayName}
                    </Link>
                    <TagChips tags={s.tags} className="mt-1" />
                    <div className="text-xs text-gray-500">
                      {SUBJECT_KIND_LABEL[s.kind]} · {s.areas.map((a) => a.region).join(', ')} ·{' '}
                      {s.verticals.map((v) => VERTICAL_LABEL[v]).join(', ')}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-semibold tabular-nums text-gray-900">{compactGhs(purposeValue)}</div>
                    <div className="mt-1 flex flex-col items-end gap-1">
                      <StatusBadge label={AVAILABILITY_LABEL[av as Availability]} className={AVAILABILITY_BADGE[av]} />
                      <StatusBadge
                        label={ACQUISITION_STAGE_LABEL[s.acquisitionStage]}
                        className={ACQUISITION_STAGE_BADGE[s.acquisitionStage]}
                      />
                    </div>
                  </div>
                </div>

                <p className="text-xs text-gray-600">
                  {onFf ? (
                    <>
                      On FeelyFeely
                      {snap?.uid != null ? ` · uid ${snap.uid}` : snap?.oid != null ? ` · oid ${snap.oid}` : ''}
                      {` · ${snap?.listingCount ?? 0} listings`}
                      {snap?.lifetimePaidGhs ? ` · paid ${compactGhs(snap.lifetimePaidGhs)}` : ''}
                    </>
                  ) : (
                    'Not on FeelyFeely yet'
                  )}
                </p>

                <p className="mt-2 text-sm text-gray-800">
                  {lock ? (
                    <>
                      {CLAIM_STATUS_LABEL[status ?? lock.status]}
                      {purposeById(lock.purposeId) ? ` · ${purposeById(lock.purposeId)?.name}` : ''}
                      {' · '}
                      {mine ? 'your exclusive' : `held by ${holder?.name ?? lock.ownerId}`}
                      {' · '}
                      <span className={lock.expiresAt && new Date(lock.expiresAt).getTime() <= now ? 'text-rose-700' : 'text-gray-500'}>
                        {timeLeftLabel(lock.expiresAt, now)}
                      </span>
                    </>
                  ) : last ? (
                    <>
                      {OUTREACH_CHANNEL_LABEL[last.channel]} · {new Date(last.at).toLocaleDateString('en-GB')}
                      {last.body ? ` — ${last.body.slice(0, 72)}` : ''}
                    </>
                  ) : (
                    <span className="text-gray-500">No conversation yet</span>
                  )}
                </p>
                {last?.nextStepDue && (
                  <p className="mt-0.5 text-[11px] text-gray-400">
                    Next {new Date(last.nextStepDue).toLocaleString('en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}

                <p className="mt-2 text-[11px] text-gray-400">
                  {sourceLabel(s.source)} · {SALES_GOAL_LABEL[scored.goal]} · fit {Math.round(scored.score * 100)}
                  {s.subjectCooldownUntil && av === 'cooling'
                    ? ` · cools until ${new Date(s.subjectCooldownUntil).toLocaleDateString()}`
                    : ''}
                </p>

                <div className="mt-auto pt-3">
                  <ClaimAction
                    available={av === 'available'}
                    mine={mine}
                    locked={Boolean(lock)}
                    cooling={av === 'cooling'}
                    holderName={holder?.name ?? lock?.ownerId}
                    until={lock ? new Date(lock.expiresAt).toLocaleDateString('en-GB') : undefined}
                    coolingUntil={s.subjectCooldownUntil ? new Date(s.subjectCooldownUntil).toLocaleDateString('en-GB') : undefined}
                    onClaim={() => openClaim(s)}
                    onClearCooldown={clearCooldown ? () => clearCooldown(s) : undefined}
                    onOpen={() => navigate(`/admin/sales/prospects/${s.id}`)}
                        subjectId={s.id}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {total > limit && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>
            {offset + 1}–{Math.min(offset + rows.length, total)} of {total}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className={btnGhost}
              disabled={page <= 1}
              onClick={() => handlePageChange(page - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className={btnGhost}
              disabled={offset + rows.length >= total}
              onClick={() => handlePageChange(page + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {claiming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">Claim {claiming.displayName}</h3>
            <p className="mb-3 text-sm text-gray-500">
              You get <span className="font-medium text-gray-800">{claimDays} exclusive days</span> to convert. Nobody else can
              claim this person until you win, lose, release, or the lock expires.
            </p>
            {onPlatform(claiming) && (
              <p className="mb-3 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                Already on FeelyFeely as {ACQUISITION_STAGE_LABEL[claiming.acquisitionStage].toLowerCase()}
                {claiming.platform?.uid != null ? ` · uid ${claiming.platform.uid}` : ''}.
                {claiming.platform?.listingCount
                  ? ` ${claiming.platform.listingCount} listing${claiming.platform.listingCount === 1 ? '' : 's'}.`
                  : ''}
              </p>
            )}
            <label className="mb-2 block text-xs text-gray-500">Purpose</label>
            <select className={`${selectCls} mb-3 w-full`} value={purposeId} onChange={(e) => setPurposeId(e.target.value)}>
              {purposes
                .filter((p) => p.active && !achievedGoalsFor(claiming.id, claims).includes(p.goal))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({SALES_GOAL_LABEL[p.goal]}) · GHS {p.estimatedValueGhs} · {p.defaultClaimDays}d exclusive
                  </option>
                ))}
            </select>
            <label className="mb-2 block text-xs text-gray-500">Offer</label>
            <select className={`${selectCls} mb-4 w-full`} value={offerId} onChange={(e) => setOfferId(e.target.value)}>
              <option value="">None</option>
              {offers
                .filter((o) => o.active)
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
            </select>
            <div className="flex justify-end gap-2">
              <button className={btnGhost} onClick={() => setClaiming(null)}>
                Cancel
              </button>
              <button className={btnPrimary} onClick={submitClaim} disabled={!purposeId}>
                Claim exclusive
              </button>
            </div>
          </div>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-base font-semibold text-gray-900">Add prospect</h3>
            <p className="mb-4 text-sm text-gray-500">
              Add someone from your book — a person or an organization. Dedupes on phone, email, or FeelyFeely id. If they’re already
              on FeelyFeely, bind the uid so we can see conversation and spend.{' '}
              <button type="button" className="font-medium text-gray-800 underline-offset-2 hover:underline" onClick={openImport}>
                Import a list instead
              </button>
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-xs text-gray-500 sm:col-span-2">
                Name
                <input
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.displayName}
                  onChange={(e) => setManual((m) => ({ ...m, displayName: e.target.value }))}
                  placeholder="Ama Realty"
                />
              </label>
              <label className="block text-xs text-gray-500">
                Phone
                <input
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.phone}
                  onChange={(e) => setManual((m) => ({ ...m, phone: e.target.value }))}
                  placeholder="0244000111"
                />
              </label>
              <label className="block text-xs text-gray-500">
                Email
                <input
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.email}
                  onChange={(e) => setManual((m) => ({ ...m, email: e.target.value }))}
                  placeholder="ama@x.com"
                />
              </label>
              <label className="block text-xs text-gray-500">
                Kind
                <select
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.kind}
                  onChange={(e) => setManual((m) => ({ ...m, kind: e.target.value as SubjectKind }))}
                >
                  {(['person', 'organization', 'business'] as SubjectKind[]).map((k) => (
                    <option key={k} value={k}>
                      {SUBJECT_KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-gray-500">
                Region
                <input
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.region}
                  onChange={(e) => setManual((m) => ({ ...m, region: e.target.value }))}
                />
              </label>
              <label className="block text-xs text-gray-500">
                Category
                <select
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.vertical}
                  onChange={(e) => setManual((m) => ({ ...m, vertical: e.target.value as SalesVertical }))}
                >
                  {SALES_VERTICALS.map((v) => (
                    <option key={v} value={v}>
                      {VERTICAL_LABEL[v]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-gray-500">
                Spend band
                <select
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.spendBand}
                  onChange={(e) => setManual((m) => ({ ...m, spendBand: e.target.value as SpendBand }))}
                >
                  {(['unknown', 'low', 'mid', 'high'] as SpendBand[]).map((b) => (
                    <option key={b} value={b}>
                      {SPEND_BAND_LABEL[b]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-xs text-gray-500 sm:col-span-2">
                Notes
                <input
                  className={`${selectCls} mt-1 w-full`}
                  value={manual.notes}
                  onChange={(e) => setManual((m) => ({ ...m, notes: e.target.value }))}
                  placeholder="My agent from East Legon"
                />
              </label>
              <div className="block text-xs text-gray-500 sm:col-span-2">
                <label htmlFor="manual-tags">Tags</label>
                <div className="mt-1">
                  <TagInput id="manual-tags" value={manual.tags} onChange={(tags) => setManual((m) => ({ ...m, tags }))} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-gray-600 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={manual.alreadyOnPlatform}
                  onChange={(e) => setManual((m) => ({ ...m, alreadyOnPlatform: e.target.checked }))}
                />
                Already on FeelyFeely
              </label>
              {manual.alreadyOnPlatform && (
                <>
                  <label className="block text-xs text-gray-500">
                    uid
                    <input
                      className={`${selectCls} mt-1 w-full`}
                      value={manual.uid}
                      onChange={(e) => setManual((m) => ({ ...m, uid: e.target.value }))}
                      placeholder="91001"
                    />
                  </label>
                  <label className="block text-xs text-gray-500">
                    oid
                    <input
                      className={`${selectCls} mt-1 w-full`}
                      value={manual.oid}
                      onChange={(e) => setManual((m) => ({ ...m, oid: e.target.value }))}
                      placeholder="401"
                    />
                  </label>
                </>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                className={btnGhost}
                onClick={() => {
                  setAddOpen(false);
                  setManual(emptyManual);
                }}
              >
                Cancel
              </button>
              <button className={btnPrimary} onClick={() => submitManual()} disabled={!manual.displayName.trim()}>
                Add to pool
              </button>
            </div>
          </div>
        </div>
      )}

      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div
            role="dialog"
            aria-labelledby="import-list-title"
            aria-modal="true"
            className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-white p-5 shadow-xl"
          >
            <h3 id="import-list-title" className="text-base font-semibold text-gray-900">
              Import a list
            </h3>
            {importResult ? (
              <>
                <p className="mt-1 text-sm text-gray-600">
                  {importResult.imported
                    ? `${importResult.imported} added to the pool.`
                    : 'Nothing new was added.'}
                  {importResult.skipped.length
                    ? ` ${importResult.skipped.length} skipped.`
                    : ''}
                </p>
                {importResult.skipped.length > 0 && (
                  <ul className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-gray-200 text-sm">
                    {importResult.skipped.map((s) => (
                      <li key={`${s.line ?? s.name}-${s.reason}`} className="border-t border-gray-100 px-3 py-2 text-gray-700 first:border-t-0">
                        {s.name ?? `Line ${s.line}`}:{' '}
                        {s.reason === 'possible_duplicate' && s.matches?.length
                          ? `looks like ${s.matches.map((m) => m.displayName).join(', ')} — add by hand if different`
                          : s.reason === 'identity_collision'
                            ? 'already in the pool'
                            : s.reason}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-5 flex justify-end gap-2">
                  <button type="button" className={btnGhost} onClick={openImport}>
                    Import another
                  </button>
                  <button
                    type="button"
                    className={btnPrimary}
                    onClick={() => {
                      setImportOpen(false);
                      setCsv('');
                      setCsvFileName('');
                      setImportResult(null);
                    }}
                  >
                    Done
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-1 text-sm text-gray-500">
                  CSV from the floor sits next to prospect records. Columns: name, phone, email, kind, region, vertical,
                  spendBand, tags (several separated by ; ).
                </p>
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  <button
                    type="button"
                    className="font-medium text-gray-800 underline-offset-2 hover:underline"
                    onClick={() => {
                      setCsv(SAMPLE_CSV);
                      setCsvFileName('');
                    }}
                  >
                    Use sample
                  </button>
                  <button
                    type="button"
                    className="font-medium text-gray-800 underline-offset-2 hover:underline"
                    onClick={() => {
                      const url = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: 'text/csv' }));
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'prospects-template.csv';
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  >
                    Download template
                  </button>
                </div>
                <label
                  className={`mt-4 flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-4 py-6 text-center transition-colors ${
                    csvDrag ? 'border-slate-900 bg-slate-50' : 'border-gray-400 bg-gray-50'
                  }`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setCsvDrag(true);
                  }}
                  onDragLeave={() => setCsvDrag(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setCsvDrag(false);
                    const file = e.dataTransfer.files[0];
                    if (file) readCsvFile(file);
                  }}
                >
                  <Upload className="mb-2 h-5 w-5 text-gray-600" aria-hidden />
                  <span className="text-sm font-medium text-gray-900">
                    {csvFileName || 'Drop a CSV, or click to choose'}
                  </span>
                  <span className="mt-1 text-xs text-gray-600">.csv or paste below</span>
                  <input
                    type="file"
                    accept=".csv,text/csv,text/plain"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) readCsvFile(file);
                      e.target.value = '';
                    }}
                  />
                </label>
                <label htmlFor="prospect-csv" className="mt-4 block text-xs font-medium text-gray-800">
                  Or paste rows
                </label>
                <textarea
                  id="prospect-csv"
                  value={csv}
                  onChange={(e) => {
                    setCsv(e.target.value);
                    setCsvFileName('');
                    setImportResult(null);
                  }}
                  rows={5}
                  className="mt-1 w-full rounded-lg border border-gray-400 p-2 font-mono text-xs text-gray-900 focus:border-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900"
                  placeholder="Ama Realty,0244000111,ama@x.com,business,Accra,homes,high"
                />
                {csvPreview.length > 0 && (
                  <div className="mt-3 max-h-48 overflow-auto rounded-lg border border-gray-200">
                    <table className="min-w-full text-xs">
                      <caption className="sr-only">Rows that will be imported</caption>
                      <thead className="sticky top-0 bg-gray-50 text-left font-medium text-gray-800">
                        <tr>
                          <th scope="col" className="px-3 py-2">
                            Name
                          </th>
                          <th scope="col" className="px-3 py-2">
                            Phone
                          </th>
                          <th scope="col" className="px-3 py-2">
                            Kind
                          </th>
                          <th scope="col" className="px-3 py-2">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {csvPreview.map((row) => (
                          <tr key={row.line} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-900">{row.displayName || '—'}</td>
                            <td className="px-3 py-2 text-gray-700">{row.phone || '—'}</td>
                            <td className="px-3 py-2 text-gray-700">{SUBJECT_KIND_LABEL[row.kind]}</td>
                            <td className={row.error ? 'px-3 py-2 text-rose-700' : 'px-3 py-2 text-emerald-700'}>
                              {row.error ?? 'Ready'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <div className="mt-5 flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-600">
                    {csvPreview.length
                      ? `${csvReady} of ${csvPreview.length} ready`
                      : 'Phone or email helps us dedupe against the pool.'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={btnGhost}
                      onClick={() => {
                        setImportOpen(false);
                        setCsv('');
                        setCsvFileName('');
                        setImportResult(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button type="button" className={btnPrimary} onClick={runImport} disabled={csvReady === 0}>
                      Import {csvReady ? csvReady : ''}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {dupes ? (
        <DuplicateMatchesDialog
          name={manual.displayName}
          matches={dupes}
          busy={createMut.isPending}
          onOpen={(id) => navigate(`/admin/sales/prospects/${id}`)}
          onConfirm={() => submitManual(true)}
          onCancel={() => setDupes(null)}
        />
      ) : null}
    </SalesPageShell>
  );
}
